// Admin management APIs for system configuration

// The backend scopes org-specific resources by an X-Org-ID header on flat routes
// (e.g. GET /api/radios), not by an org id in the URL path.
const orgFetch = (orgId: string, path: string, init: RequestInit = {}): Promise<Response> =>
  fetch(path, {
    ...init,
    headers: { 'X-Org-ID': orgId, ...(init.headers || {}) },
  });

export interface Organization {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  radio_count?: number;
  group_count?: number;
  user_count?: number;
}

export interface User {
  id: string;
  org_id: string;
  username: string;
  email: string;
  role: 'admin' | 'dispatcher' | 'observer';
  created_at: string;
  updated_at: string;
}

export interface PendingRadio {
  id: string;
  serial: string;
  model: string;
  android_version: string;
  firmware: string;
  android_id: string;
  iccid: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_at: string;
  created_at?: string;
}

export interface RadioAvailabilityStatus {
  id: string;
  org_id: string;
  name: string;
  sort_order: number;
  is_default: boolean;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface Radio {
  id: string;
  org_id: string;
  account_id?: string | null;
  radio_id: string;
  serial: string;
  model: string;
  name: string;
  android_version?: string;
  firmware?: string;
  android_id?: string;
  iccid?: string;
  status: 'online' | 'offline' | 'idle' | 'transmitting' | 'low_battery';
  availability_status_id?: string | null;
  availability_status_name?: string | null;
  availability_status_color?: string | null;
  battery_level: number;
  charging_state?: string | null;
  // Mobile-operator-selection telemetry (see documentation/decisions.md "S380 operator
  // selection capability"), kept live via heartbeat - shown as the "Operator" column.
  network_operator?: string | null;
  network_operator_numeric?: string | null;
  network_selection_mode?: string | null;
  priority: 'low' | 'standard' | 'high';
  last_seen: string;
  primary_group_id?: string | null;
  primary_group_name?: string | null;
  startup_group_id?: string | null;
  // Radio-specific settings (Admin > Radios > Edit > "Radio-Specific Settings") - group-name
  // text-to-speech announcement on the Android app.
  tts_enabled: boolean;
  tts_language: 'da' | 'en';
  ptt_tone_enabled: boolean;
  busy_tone_enabled: boolean;
  connection_tts_enabled: boolean;
  incoming_ptt_start_enabled: boolean;
  incoming_ptt_stop_enabled: boolean;
  sos_enabled: boolean;
  sos_alarm_sound_enabled: boolean;
  sos_visual_enabled?: boolean;
  sos_recipient_mode: 'dispatcher' | 'radio';
  sos_recipient_radio_ids?: string[];
  low_battery_threshold_percent: number;
  low_battery_tts_enabled: boolean;
  message_retention_days?: number;
  offline_location_retention_days?: number;
  wifi_enabled?: boolean | null;
  wifi_ssid?: string | null;
  wifi_operation_state?: string | null;
  messages_enabled?: boolean;
  status_enabled?: boolean;
  // DMR (IRC380): the per-radio setting, and whether the canonical model has a DMR module.
  dmr_enabled?: boolean;
  dmr_supported?: boolean;
  // PoC (all models, default true). false = no Primary Group, Secondary Group or direct PoC audio
  // (RX and TX), enforced by the server; the radio stays online and keeps its group assignments.
  poc_enabled?: boolean;
  // Feature codes this radio's canonical model does not support (the *_enabled fields stay the
  // radio's own preference; the effective feature is preference AND supported).
  unsupported_features?: string[];
  // Hides the Settings (gear icon) on the Android app while connected+approved - always shown
  // while disconnected or not yet approved.
  hide_settings_on_radio: boolean;
  // Shared volume (0-100, default 8) for every app-generated local system sound (PTT chirp,
  // channel-busy tone, other local signal/status tones) - never received LiveKit voice audio,
  // the mic's gain, or the radio's global Android volume.
  system_sound_volume_percent: number;
  // On-device sustained-tilt "Man Down" fall detection (see documentation/protocol.md "Fall
  // detection / Man Down") - disabled by default; sensitivity 1-3 (low=75deg/medium=60deg/
  // high=45deg tilt trigger), tilt delay 1-60s (how long tilted before the pre-alarm starts),
  // prealarm 5-60s (the pre-alarm's own countdown).
  fall_detection_enabled: boolean;
  fall_detection_sensitivity: number;
  fall_detection_tilt_delay_seconds: number;
  fall_detection_prealarm_seconds: number;
  // Prealarm alarm-sound settings - separate from the mandatory countdown above; disabling/
  // quieting the sound never disables vibration/screen wake/the countdown/the alarm event
  // itself. Never affects (or is affected by) system_sound_volume_percent or any radio talk
  // volume (see documentation/protocol.md - a prior implementation that temporarily maxed
  // STREAM_ALARM was found to also change the radio's talk volume on this hardware).
  fall_detection_alarm_sound_enabled: boolean;
  fall_detection_alarm_volume_percent: number;
  // Radio/User picker box toggle on the Android app (see documentation/protocol.md "Radio
  // directory") - defaults true (opt-out).
  direct_call_enabled: boolean;
  // Secondary Group feature toggle on the Android app (see documentation/protocol.md "Secondary
  // Group") - defaults false (opt-in). Admin-editable here, but can also be selected/enabled
  // directly on the radio itself (Secondary Group box).
  secondary_group_enabled: boolean;
  secondary_group_id?: string | null;
  secondary_group_name?: string | null;
  auto_mute_secondary_when_primary_receiving: boolean;
  // Received (RX) voice audio gain boost applied on the Android app (see AudioService.kt) -
  // 1.0 = normal/unity gain (default), bounded 1.0-4.0.
  rx_audio_gain: number;
  // Global per-radio block (Admin > Radios > "Blocked" column) - when true, the radio cannot
  // request/hold the floor on any group; the radio itself shows a persistent banner naming who
  // blocked it and why.
  blocked: boolean;
  blocked_by_username?: string | null;
  blocked_at?: string | null;
  blocked_reason?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Group {
  id: string;
  org_id: string;
  name: string;
  description: string;
  group_type: string;
  priority: 'low' | 'standard' | 'high';
  echo: boolean;
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface GroupMember {
  id: string;
  group_id: string;
  radio_id: string;
  radio_name: string;
  role: 'member' | 'admin';
  ptt_blocked: boolean;
  joined_at: string;
}

export interface ProvisioningToken {
  id: string;
  org_id: string;
  token: string;
  used: boolean;
  used_by: string | null;
  created_at: string;
  expires_at: string;
}

export interface FloorEvent {
  id: string;
  org_id: string;
  group_id: string;
  group_name: string;
  radio_id: string;
  radio_name: string;
  event_type: 'floor_request' | 'floor_grant' | 'floor_release';
  duration_ms: number | null;
  created_at: string;
}

export interface InricoRadio {
  id: string;
  serial_number: string;
  android_id: string;      // Unique device identifier (Android ID; will be real IMEI in the future)
  iccid: string;          // SIM Card ID
  model: string;
  mac_address: string;
  firmware_version: string;
  authorized: boolean;
  registration_status: string;
  last_heartbeat: string | null;
  created_at: string;
}

// Users API
export const getUsersByOrg = async (orgId: string): Promise<User[]> => {
  const response = await orgFetch(orgId, '/api/admin/users');
  if (!response.ok) throw new Error('Failed to fetch users');
  // Backend returns null (not []) for an empty list; normalize to an array.
  return (await response.json()) ?? [];
};

export const createUser = async (orgId: string, user: Omit<User, 'id' | 'created_at' | 'updated_at'>): Promise<User> => {
  const response = await orgFetch(orgId, '/api/admin/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(user),
  });
  if (!response.ok) throw new Error('Failed to create user');
  return response.json();
};

export const updateUser = async (orgId: string, userId: string, updates: Partial<User>): Promise<User> => {
  const response = await orgFetch(orgId, `/api/admin/users/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Failed to update user');
  return response.json();
};

export const deleteUser = async (orgId: string, userId: string): Promise<void> => {
  const response = await orgFetch(orgId, `/api/admin/users/${userId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete user');
};

// Radios API
export const getRadiosByOrg = async (orgId: string): Promise<Radio[]> => {
  const response = await orgFetch(orgId, '/api/radios');
  if (!response.ok) throw new Error('Failed to fetch radios');
  return (await response.json()) ?? [];
};

export const createRadio = async (orgId: string, radio: Omit<Radio, 'id' | 'created_at' | 'updated_at'>): Promise<Radio> => {
  const response = await orgFetch(orgId, '/api/radios', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(radio),
  });
  if (!response.ok) throw new Error('Failed to create radio');
  return response.json();
};

export const updateRadio = async (orgId: string, radioId: string, updates: Partial<Radio>): Promise<Radio> => {
  const response = await orgFetch(orgId, `/api/radios/${radioId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Failed to update radio');
  return response.json();
};

export const deleteRadio = async (orgId: string, radioId: string): Promise<void> => {
  const response = await orgFetch(orgId, `/api/radios/${radioId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete radio');
};

// Radio-to-network assignment (admin, across all networks)
export interface RadioWithOrg {
  id: string;
  org_id: string | null;
  org_name: string | null;
  radio_id: string;
  model: string | null;
  name: string | null;
  status: string;
  android_id: string | null; // Unique device identifier (Android ID; will be real IMEI in the future), if an Inrico device
  primary_group_id: string | null;
  primary_group_name: string | null;
  // Fix time (when the GPS fix was taken, not when it was received) of the latest known location
  // in the radio's current network, if any; full detail is on the device-details page
  // (documentation/protocol.md §12).
  last_location_at: string | null;
  // Current GPS availability from the radio's latest heartbeat: 'valid' | 'not_available'.
  gps_status?: string | null;
  priority: 'low' | 'standard' | 'high';
  // Looked up from Model Catalog (device_models.device_type) by matching model - null if this
  // model isn't catalogued yet.
  device_type: string | null;
  // Currently-installed app build, as last reported over heartbeat, and the newest build
  // published in the APK Repository for this radio's model (null if none) - see
  // documentation/protocol.md "OTA app updates".
  app_version_code: number | null;
  app_version_name: string | null;
  latest_version_code: number | null;
  latest_version_name: string | null;
  system_sound_volume_percent: number;
  fall_detection_enabled: boolean;
  fall_detection_sensitivity: number;
  fall_detection_tilt_delay_seconds: number;
  fall_detection_prealarm_seconds: number;
  fall_detection_alarm_sound_enabled: boolean;
  fall_detection_alarm_volume_percent: number;
  // Radio/User picker box toggle (see documentation/protocol.md "Radio directory") - defaults
  // true (opt-out).
  direct_call_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export const getAllRadios = async (): Promise<RadioWithOrg[]> => {
  const response = await fetch('/api/admin/radios/all');
  if (!response.ok) throw new Error('Failed to fetch radios');
  return (await response.json()) ?? [];
};

export const getRadioAvailabilityStatuses = async (orgId: string): Promise<RadioAvailabilityStatus[]> => {
  const response = await orgFetch(orgId, `/api/admin/organizations/${orgId}/radio-statuses`);
  if (!response.ok) throw new Error('Failed to fetch radio status catalog');
  return (await response.json()) ?? [];
};

export const createRadioAvailabilityStatus = async (orgId: string, name: string, color: string): Promise<RadioAvailabilityStatus> => {
  const response = await orgFetch(orgId, `/api/admin/organizations/${orgId}/radio-statuses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, color }),
  });
  if (!response.ok) throw new Error('Failed to create radio status');
  return response.json();
};

export const updateRadioAvailabilityStatus = async (orgId: string, id: string, name: string, color: string): Promise<void> => {
  const response = await orgFetch(orgId, `/api/admin/organizations/${orgId}/radio-statuses/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, color }),
  });
  if (!response.ok) throw new Error('Failed to update radio status');
};

export const deleteRadioAvailabilityStatus = async (orgId: string, id: string): Promise<void> => {
  const response = await orgFetch(orgId, `/api/admin/organizations/${orgId}/radio-statuses/${id}`, { method: 'DELETE' });
  if (!response.ok) throw new Error('Failed to delete radio status');
};

export const setRadioAvailabilityStatus = async (orgId: string, radioId: string, statusId: string | null): Promise<void> => {
  const response = await orgFetch(orgId, `/api/admin/radios/${radioId}/availability-status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ availability_status_id: statusId }),
  });
  if (!response.ok) throw new Error('Failed to update radio status');
};

export const assignRadioOrg = async (radioId: string, orgId: string | null): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/org`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ org_id: orgId }),
  });
  if (!response.ok) throw new Error('Failed to assign radio');
};

export const deleteRadioById = async (radioId: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}`, { method: 'DELETE' });
  if (!response.ok) throw new Error('Failed to delete radio');
};

// Global per-radio block (Admin > Radios > "Blocked" column) - see Radio.blocked's doc comment.
export const blockRadio = async (radioId: string, reason?: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/block`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || undefined }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to block radio');
};

export const unblockRadio = async (radioId: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/unblock`, { method: 'POST' });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to unblock radio');
};

export const sendCallAlert = async (radioId: string, orgId?: string, message?: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/call-alert`, {
    method: 'POST',
    headers: {
      ...(orgId ? { 'X-Org-ID': orgId } : {}),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ message: message || '' }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to send Call Alert');
};

export const setMessageRetentionDays = async (radioId: string, days: number): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/message-retention`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message_retention_days: days }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to save message retention');
};

export const setOfflineLocationRetentionDays = async (radioId: string, days: number): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/offline-location-retention`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ offline_location_retention_days: days }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to save offline GPS retention');
};

// Radio button assignments (documentation/protocol.md "Radio button assignments").
export interface ButtonAssignmentAction {
  code: string;
  name: string;
  required_feature: string | null;
  interaction: 'trigger' | 'hold' | string;
  trigger_event: string | null;
}

export interface AssignableButton {
  button_function_id: string;
  name: string;
  events: string[];
  /** null = no assignment: the button keeps its built-in behaviour. */
  action_code: string | null;
  allowed_actions: string[];
}

export interface RadioButtonAssignments {
  radio_id: string;
  device_model_id: string | null;
  actions: ButtonAssignmentAction[];
  buttons: AssignableButton[];
}

const errorText = async (response: Response, fallback: string): Promise<string> => {
  const text = await response.text();
  try {
    return JSON.parse(text).error || fallback;
  } catch {
    return text || fallback;
  }
};

export const getRadioButtonAssignments = async (radioId: string): Promise<RadioButtonAssignments> => {
  const response = await fetch(`/api/admin/radios/${radioId}/button-assignments`);
  if (!response.ok) throw new Error(await errorText(response, 'Failed to load button assignments'));
  return response.json();
};

export const setRadioButtonAssignments = async (
  radioId: string,
  assignments: { button_function_id: string; action_code: string }[]
): Promise<RadioButtonAssignments> => {
  const response = await fetch(`/api/admin/radios/${radioId}/button-assignments`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assignments }),
  });
  if (!response.ok) throw new Error(await errorText(response, 'Failed to save button assignments'));
  return response.json();
};

export const setDmrEnabled = async (radioId: string, enabled: boolean): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/dmr-enabled`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dmr_enabled: enabled }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to save DMR setting');
};

// PoC on/off for one radio (any model). Off = no Primary Group, Secondary Group or direct PoC
// audio, enforced by the server; the radio stays online and keeps its group assignments.
export const setPocEnabled = async (radioId: string, enabled: boolean): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/poc-enabled`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ poc_enabled: enabled }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to save PoC setting');
};

export const setStatusEnabled = async (radioId: string, enabled: boolean): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/status-enabled`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status_enabled: enabled }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to save Status setting');
};

export const setMessagesEnabled = async (radioId: string, enabled: boolean): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/messages-enabled`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages_enabled: enabled }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to save Messages setting');
};

export const sendDirectMessage = async (radioId: string, message: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to send message');
};

// The server sends only to the network it authorized in X-Org-ID; org_id in the body must match it.
export const sendDirectMessageToAll = async (orgId: string, message: string): Promise<{ sent: number; failed: number }> => {
  const response = await orgFetch(orgId, '/api/admin/radios/message-all', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ org_id: orgId, message }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to send message to all radios');
  return response.json();
};

export const updateRadioDetails = async (
  radioId: string,
  updates: {
    radio_id: string;
    model: string;
    name: string;
    primary_group_id?: string | null;
    startup_group_id?: string | null;
    priority?: string;
    tts_enabled?: boolean;
    tts_language?: string;
    ptt_tone_enabled?: boolean;
    busy_tone_enabled?: boolean;
    connection_tts_enabled?: boolean;
    incoming_ptt_start_enabled?: boolean;
    incoming_ptt_stop_enabled?: boolean;
    sos_enabled?: boolean;
    sos_alarm_sound_enabled?: boolean;
    sos_recipient_mode?: 'dispatcher' | 'radio';
    sos_recipient_radio_ids?: string[];
    low_battery_threshold_percent?: number;
    low_battery_tts_enabled?: boolean;
    hide_settings_on_radio?: boolean;
    // 0-100; omit to leave the radio's existing value untouched (never resets it).
    system_sound_volume_percent?: number;
    // Fall Detection / Man Down (see documentation/protocol.md "Fall detection / Man Down") -
    // each omittable independently to leave the radio's existing value untouched (never resets it).
    fall_detection_enabled?: boolean;
    fall_detection_sensitivity?: number;
    fall_detection_tilt_delay_seconds?: number;
    fall_detection_prealarm_seconds?: number;
    fall_detection_alarm_sound_enabled?: boolean;
    fall_detection_alarm_volume_percent?: number;
    // Radio/User picker box toggle - omittable to leave the radio's existing value untouched.
    direct_call_enabled?: boolean;
    // Secondary Group - primary_group_id-style convention: always sent, null clears it.
    secondary_group_id?: string | null;
    // Secondary Group feature toggle - omittable to leave the radio's existing value untouched.
    secondary_group_enabled?: boolean;
    auto_mute_secondary_when_primary_receiving?: boolean;
    // Received (RX) voice audio gain boost (1.0-4.0) - omittable to leave the radio's existing
    // value untouched.
    rx_audio_gain?: number;
  }
): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to update radio');
};

export const getRadioSOSSettings = async (radioId: string): Promise<{ sos_recipient_radio_ids: string[] }> => {
  const response = await fetch(`/api/admin/radios/${radioId}/sos-settings`);
  if (!response.ok) throw new Error('Failed to fetch SOS settings');
  return response.json();
};

export const updateRadioSOSSettings = async (radioId: string, settings: {
  sos_enabled: boolean;
  sos_alarm_sound_enabled: boolean;
  sos_visual_enabled: boolean;
  sos_recipient_mode: 'dispatcher' | 'radio';
  sos_recipient_radio_ids?: string[];
}): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/sos-settings`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(settings),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to update SOS settings');
};

export const getOrganizationSOSSettings = async (orgId: string): Promise<{ sos_recipient_mode: 'dispatcher' | 'radio'; sos_recipient_radio_ids: string[] }> => {
  const response = await fetch(`/api/admin/organizations/${orgId}/sos-settings`);
  if (!response.ok) throw new Error('Failed to fetch network SOS settings');
  return response.json();
};

export const updateOrganizationSOSSettings = async (orgId: string, settings: {
  sos_recipient_mode: 'dispatcher' | 'radio';
  sos_recipient_radio_ids: string[];
}): Promise<void> => {
  const response = await fetch(`/api/admin/organizations/${orgId}/sos-settings`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(settings),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to update network SOS settings');
};

// Device info (latest reported device/system telemetry, from Android registration - documentation/protocol.md §5)
// Conventional radio (IRC380 DMR) state as last reported by the radio (protocol "Conventional
// radio (DMR) status"). Frequencies in Hz.
export interface DmrStatus {
  availability: string;
  power: string;
  module_model: string | null;
  hardware_version: string | null;
  firmware_version: string | null;
  protocol_version: string | null;
  data_version: string | null;
  sdk_version: string | null;
  frequency_scope: number | null;
  frequency_range: string | null;
  min_frequency: number | null;
  max_frequency: number | null;
  radio_id: number | null;
  zone_number: number | null;
  zone_name: string | null;
  channel_number: number | null;
  channel_name: string | null;
  channel_type: string | null;
  rx_frequency_hz: number | null;
  tx_frequency_hz: number | null;
  color_code: number | null;
  time_slot: number | null;
  call_state: string;
  /** PRIMARY_GROUP or DMR: what the Main PTT (and a remote speaker mic) drives. */
  main_ptt_target: string;
  last_error_code: number | null;
  last_error: string | null;
  last_error_at: string | null;
  /** SERVER: the radio follows dmr_enabled; LOCAL: the operator enabled DMR locally on the radio
   *  (revoked by the next explicit DMR change here, or by the server setting once the radio
   *  reconnects after losing its connection). Null/absent: an app that does not report it. */
  dmr_source?: 'SERVER' | 'LOCAL' | null;
  reported_at: string;
}

export interface DeviceInfo {
  // The canonical model has a DMR module; dmr is null until the radio reports.
  dmr_supported?: boolean;
  dmr?: DmrStatus | null;
  id: string;
  radio_id: string;
  name: string | null;
  model: string | null;
  status: string;
  org_name: string | null;
  android_id: string | null;
  iccid: string | null;
  /** True when the server masked the ICCID (callers outside the platform). */
  iccid_masked?: boolean;
  imei1: string | null;
  imei2: string | null;
  product: string | null;
  android_version: string | null;
  build_display: string | null;
  sim_operator_name: string | null;
  network_operator: string | null;
  network_type: string | null;
  signal_strength_dbm: number | null;
  ip_addresses: string[] | null;
  battery_level: number | null;
  battery_health: string | null;
  charging_state: string | null;
  battery_temperature_c: number | null;
  display_width: number | null;
  display_height: number | null;
  display_density_dpi: number | null;
  device_info_updated_at: string | null;
  // Latest known GPS location (documentation/protocol.md). Units: latitude/longitude in decimal degrees,
  // accuracy_m/altitude_m in meters, speed_mps in meters/second, bearing_deg in degrees (0-360).
  // location_timestamp is when the fix was taken; location_received_at is when the server got it.
  latitude: number | null;
  longitude: number | null;
  accuracy_m: number | null;
  altitude_m: number | null;
  speed_mps: number | null;
  bearing_deg: number | null;
  location_provider: string | null;
  location_timestamp: string | null;
  location_received_at: string | null;
  gps_status?: string | null; // current availability, latest heartbeat
  // Device Details page diagnostics (documentation/protocol.md "Device power actions"/"Device logs").
  hardware_serial: string | null;
  uptime_seconds: number | null;
  device_time: string | null;
  display_timeout_ms: number | null;
  // Mobile-operator-selection telemetry + latest scan/switch status (see
  // documentation/decisions.md "S380 operator selection capability"). Latest-known values only.
  network_operator_numeric: string | null;
  network_roaming: boolean | null;
  network_selection_mode: string | null;
  mobile_network_scan_status: string | null;
  mobile_network_scan_result: string | null; // raw JSON array, see MobileNetworkOperator below
  mobile_network_scan_updated_at: string | null;
  mobile_network_switch_state: string | null;
  mobile_network_switch_message: string | null;
  mobile_network_switch_updated_at: string | null;
  wifi_enabled: boolean | null;
  wifi_ssid: string | null;
  wifi_scan_status: string | null;
  wifi_scan_result: string | null;
  wifi_scan_updated_at: string | null;
  wifi_operation_state: string | null;
  wifi_operation_message: string | null;
  wifi_operation_updated_at: string | null;
  wifi_signal_dbm: number | null;
  wifi_link_speed_mbps: number | null;
  // Request correlation and the radio's own Wi-Fi interface reading (documentation/protocol.md "Wi-Fi
  // remote control"). wifi_telemetry_updated_at: when the server received the last reading.
  wifi_scan_request_id?: string | null;
  wifi_operation_request_id?: string | null;
  wifi_bssid?: string | null;
  wifi_frequency_mhz?: number | null;
  wifi_security?: string | null;
  wifi_ip_addresses?: string[] | null;
  wifi_telemetry_updated_at?: string | null;
  // Genuine cellular signal of the active data subscription ("Cellular signal"); never the legacy
  // mixed signal_strength_dbm. status explains a missing value.
  cellular_signal_dbm?: number | null;
  cellular_signal_status?: string | null;
  cellular_signal_technology?: string | null;
  cellular_signal_updated_at?: string | null;
  // What the installed app announced: an older app never appears to support saved networks.
  wifi_capabilities?: { background_commands: boolean; saved_networks: boolean };
  wifi_saved_networks?: WifiSavedNetworks | null;
  wifi_saved_last_operation?: WifiSavedOperation | null;
}

// A radio-reported saved (configured) Wi-Fi network: never a key (documentation/protocol.md "Saved
// Wi-Fi networks").
export interface WifiSavedNetwork {
  network_id: number;
  ssid: string;
  security: string;
  current: boolean;
}

export interface WifiSavedNetworks {
  snapshot_id: string;
  status: 'SUPPORTED' | 'UNSUPPORTED' | 'UNAVAILABLE';
  reason: string | null;
  networks: WifiSavedNetwork[];
  updated_at: string;
}

export interface WifiSavedResult {
  network_id: number;
  ssid: string;
  outcome: 'REMOVED' | 'NOT_FOUND' | 'MISMATCH' | 'FAILED' | 'SKIPPED' | 'NO_RESULT';
  code?: string;
}

export interface WifiSavedOperation {
  id: string;
  snapshot_id: string;
  mode: 'selected' | 'all';
  requested: WifiSavedNetwork[];
  requested_by_username: string;
  status: 'PENDING' | 'COMPLETED' | 'PARTIAL' | 'FAILED';
  code?: string;
  results: WifiSavedResult[];
  created_at: string;
  completed_at: string | null;
  expired: boolean;
}

// What a Wi-Fi command request returns: the id the radio's result will carry, and the server's time.
export interface WifiRequestTicket {
  request_id: string;
  requested_at: string;
}

// One operator as reported by a radio's scan result (see android/.../mobilenetwork/MobileNetworkModels.kt).
export interface MobileNetworkOperator {
  name: string | null;
  short_name: string | null;
  mcc_mnc: string | null;
  availability: 'AVAILABLE' | 'CURRENT' | 'FORBIDDEN' | 'UNKNOWN';
  technology: string;
}

export interface WifiNetwork {
  ssid: string;
  bssid?: string;
  signal_dbm: number;
  security: string;
}

export const getDeviceInfo = async (radioId: string): Promise<DeviceInfo> => {
  const response = await fetch(`/api/admin/radios/${radioId}/device-info`);
  if (!response.ok) throw new Error('Failed to fetch device info');
  return response.json();
};

// Asks a currently-connected (online) radio to re-collect and re-report its device info now,
// instead of waiting for its next registration. Only meaningful for an online radio - the
// backend returns 409 if it isn't currently connected over the WebSocket.
export const refreshDeviceInfo = async (radioId: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/device-info/refresh`, { method: 'POST' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to request device info refresh');
  }
};

// Asks a currently-connected (online) radio to check for and silently self-install a new OTA
// build right now (Assign Radios "Push Update" button, see documentation/protocol.md "OTA app updates").
// Only meaningful for an online radio - the backend returns 409 if it isn't currently connected.
export const pushUpdateToRadio = async (radioId: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/push-update`, { method: 'POST' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to push update');
  }
};

// Mobile Network remote control (Management Client Device Details > Network - see
// documentation/decisions.md "S380 operator selection capability"). All three are fire-and-forget
// commands relayed to the radio's own WebSocket connection - results arrive asynchronously and
// are read back via getDeviceInfo (mobile_network_scan_*/mobile_network_switch_* fields). The
// radio always runs its own local, backend-independent watchdog before/after a manual switch -
// this client never implements or waits on that logic itself.
export const requestMobileNetworkScan = async (radioId: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/mobile-network/scan`, { method: 'POST' });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to request a network scan');
};

export const requestMobileNetworkSwitch = async (
  radioId: string,
  mccMnc: string,
  operatorName: string | null
): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/mobile-network/switch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mcc_mnc: mccMnc, operator_name: operatorName }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to request an operator switch');
};

export const requestMobileNetworkAutomatic = async (radioId: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/mobile-network/automatic`, { method: 'POST' });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to request automatic selection');
};

// Wi-Fi remote control (documentation/protocol.md "Wi-Fi remote control"): commands relayed to the
// radio's WebSocket; the result arrives asynchronously and is read back via getDeviceInfo, matched by
// the returned request_id (wifi_scan_request_id / wifi_operation_request_id).
const wifiTicket = async (response: Response, fallback: string): Promise<WifiRequestTicket> => {
  if (!response.ok) throw new Error((await response.text()) || fallback);
  return response.json();
};

export const requestWifiScan = async (radioId: string): Promise<WifiRequestTicket> =>
  wifiTicket(await fetch(`/api/admin/radios/${radioId}/wifi/scan`, { method: 'POST' }), 'Failed to request Wi-Fi scan');

export const requestWifiEnabled = async (radioId: string, enabled: boolean): Promise<WifiRequestTicket> =>
  wifiTicket(await fetch(`/api/admin/radios/${radioId}/wifi/enabled`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled }),
  }), 'Failed to change Wi-Fi state');

// The password is sent once, in this request only; callers must clear their copy afterwards.
export const requestWifiConnect = async (radioId: string, ssid: string, password: string): Promise<WifiRequestTicket> =>
  wifiTicket(await fetch(`/api/admin/radios/${radioId}/wifi/connect`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ssid, password }),
  }), 'Failed to request Wi-Fi connection');

// Saved Wi-Fi networks: refresh asks the radio to report its list now; delete removes exactly the
// confirmed entries of the named snapshot ("all" lists every entry, never a wildcard).
export const requestSavedWifiRefresh = async (radioId: string): Promise<WifiRequestTicket> =>
  wifiTicket(await fetch(`/api/admin/radios/${radioId}/wifi/saved-networks/refresh`, { method: 'POST' }), 'Failed to request the saved networks');

export const deleteSavedWifiNetworks = async (
  radioId: string,
  snapshotId: string,
  mode: 'selected' | 'all',
  networks: Pick<WifiSavedNetwork, 'network_id' | 'ssid'>[]
): Promise<WifiSavedOperation> => {
  const response = await fetch(`/api/admin/radios/${radioId}/wifi/saved-networks/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ snapshot_id: snapshotId, mode, networks: networks.map((n) => ({ network_id: n.network_id, ssid: n.ssid })) }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to request the deletion');
  return response.json();
};

// Device Details "Device" section: Restart/Shut down buttons (documentation/protocol.md "Device power
// actions"). Both are destructive/real-world actions - the caller must confirm before invoking.
export const rebootDevice = async (radioId: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/reboot`, { method: 'POST' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to request reboot');
  }
};

export const shutdownDevice = async (radioId: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/shutdown`, { method: 'POST' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to request shutdown');
  }
};

// Device Details "Display" section: sets the device's screen-off timeout (documentation/protocol.md).
export const setDisplayTimeout = async (radioId: string, timeoutMs: number): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/display-timeout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ timeout_ms: timeoutMs }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to set display timeout');
  }
};

// Device Details "Logs" section (documentation/protocol.md "Device logs").
export interface DeviceLog {
  id: string;
  radio_id: string;
  log_type: string;
  file_size_bytes: number;
  created_at: string;
}

export const getDeviceLogs = async (radioId: string): Promise<DeviceLog[]> => {
  const response = await fetch(`/api/admin/radios/${radioId}/logs`);
  if (!response.ok) throw new Error('Failed to fetch device logs');
  return response.json();
};

export const requestLogCollection = async (radioId: string, logType: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/logs/collect?type=${encodeURIComponent(logType)}`, {
    method: 'POST',
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to request log collection');
  }
};

export const clearLogcat = async (radioId: string): Promise<void> => {
  const response = await fetch(`/api/admin/radios/${radioId}/logs/clear-logcat`, { method: 'POST' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to request logcat clear');
  }
};

export const downloadDeviceLogUrl = (logId: string): string => `/api/admin/device-logs/${logId}/download`;

export const deleteDeviceLog = async (logId: string): Promise<void> => {
  const response = await fetch(`/api/admin/device-logs/${logId}`, { method: 'DELETE' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to delete log');
  }
};

// Groups API (the backend calls these "groups"; the UI labels them "Groups" too)
export const getGroupsByOrg = async (orgId: string): Promise<Group[]> => {
  const response = await orgFetch(orgId, '/api/groups');
  if (!response.ok) throw new Error('Failed to fetch groups');
  // Backend returns null (not []) for an empty list; normalize to an array.
  return (await response.json()) ?? [];
};

export const createGroup = async (orgId: string, group: Pick<Group, 'name' | 'description' | 'group_type' | 'priority' | 'echo'>): Promise<Group> => {
  const response = await orgFetch(orgId, '/api/groups', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(group),
  });
  if (!response.ok) throw new Error('Failed to create group');
  return response.json();
};

export const updateGroup = async (
  orgId: string,
  groupId: string,
  updates: Pick<Group, 'name' | 'description' | 'group_type' | 'priority' | 'echo'>
): Promise<void> => {
  const response = await orgFetch(orgId, `/api/groups/${groupId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Failed to update group');
};

export const deleteGroup = async (orgId: string, groupId: string): Promise<void> => {
  const response = await orgFetch(orgId, `/api/groups/${groupId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete group');
};

export const deleteAllGroups = async (orgId: string): Promise<number> => {
  const response = await orgFetch(orgId, '/api/groups', {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete all groups');
  const data = await response.json();
  return data?.deleted ?? 0;
};

// Group Members API (org-scoped - the group must belong to the caller's org)
export const getGroupMembers = async (orgId: string, groupId: string): Promise<GroupMember[]> => {
  const response = await orgFetch(orgId, `/api/groups/${groupId}/members`);
  if (!response.ok) throw new Error('Failed to fetch members');
  // Backend returns null (not []) for an empty list; normalize to an array.
  return (await response.json()) ?? [];
};

export const addMember = async (orgId: string, groupId: string, radioId: string): Promise<void> => {
  const response = await orgFetch(orgId, `/api/groups/${groupId}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ radio_id: radioId }),
  });
  if (!response.ok) throw new Error('Failed to add member');
};

export const removeMember = async (orgId: string, groupId: string, radioId: string): Promise<void> => {
  const response = await orgFetch(orgId, `/api/groups/${groupId}/members/${radioId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to remove member');
};

export const setMemberBlocked = async (orgId: string, groupId: string, radioId: string, blocked: boolean): Promise<void> => {
  const response = await orgFetch(orgId, `/api/groups/${groupId}/members/${radioId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ptt_blocked: blocked }),
  });
  if (!response.ok) throw new Error('Failed to update member');
};

// Model Catalog (Settings > Model Catalog) - global, not per-org: a hardware model behaves the
// same regardless of which network its radios belong to. Auto-created (bare defaults, no admin
// input yet) whenever a not-yet-seen Android Build.MODEL enrolls - see documentation/protocol.md.
// One hardware input binding: an OEM broadcast `action`, or (action "") an Android KeyEvent
// identified by key_code + scan_code (documentation/protocol.md "Hardware input bindings").
export interface DeviceModelButtonIntent {
  id: string;
  name: string;
  action: string;
  key_code?: number;
  scan_code?: number;
}

/** Whether an intent binds a mechanism (broadcast action or KeyEvent) - unbound rows are dropped on save. */
export const intentIsBound = (i: DeviceModelButtonIntent): boolean =>
  i.action.trim() !== '' || (i.key_code != null && i.scan_code != null);

export interface DeviceModelButtonAction {
  button_function_id: string;
  intents: DeviceModelButtonIntent[];
  user_configurable: boolean;
}

export interface DeviceModel {
  id: string;
  model_name: string;
  // Canonical identity (C1), read-only via the API: both set = canonical, both null = provisional.
  manufacturer_code: string | null;
  model_code: string | null;
  display_name: string | null;
  device_type: 'radio' | 'bodyworn';
  screen_size?: string | null;
  touch_screen: boolean;
  keypad: boolean;
  button_actions: DeviceModelButtonAction[];
  // The subset of the global LED Outputs catalog this model supports, each with the model's own
  // OEM intent. Omitted from a save = keep the existing mappings; [] = no LEDs.
  led_mappings: DeviceModelLedMapping[];
  // Application feature CAPABILITIES (migration 0065): the full registry, supported or not.
  // Omitted from a save = keep; listed entries are replaced.
  features: DeviceModelFeature[];
  default_registration_url: string;
  // Hardware identity a physical unit reports over ADB (ro.product.manufacturer/ro.product.device).
  // The USB Installer needs both (with model_name) to match; null = not USB-installable.
  // Omitted from a save = keep; '' clears.
  android_manufacturer?: string | null;
  android_device?: string | null;
  // DRAFT = created by Model Discovery, not USB-installable or bindable until activated.
  lifecycle_state?: 'DRAFT' | 'ACTIVE';
  discovery_session_id?: string | null;
  auto_created: boolean;
  created_at: string;
  updated_at: string;
}

export interface DeviceModelFeature {
  code: string;
  name?: string;
  supported: boolean;
}

export interface DeviceModelLedMapping {
  led_output_id: string;
  // Returned for display; ignored on save (the output is referenced by id).
  color?: string;
  state?: string;
  intent_action: string;
}

export type DeviceModelInput = Omit<
  DeviceModel,
  'id' | 'manufacturer_code' | 'model_code' | 'display_name' | 'auto_created' | 'created_at' | 'updated_at'
>;

export const getDeviceModels = async (): Promise<DeviceModel[]> => {
  const response = await fetch('/api/admin/device-models');
  if (!response.ok) throw new Error('Failed to fetch device models');
  return (await response.json()) ?? [];
};

export const createDeviceModel = async (model: DeviceModelInput): Promise<DeviceModel> => {
  const response = await fetch('/api/admin/device-models', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(model),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to create device model');
  return response.json();
};

export const updateDeviceModel = async (id: string, model: DeviceModelInput): Promise<DeviceModel> => {
  const response = await fetch(`/api/admin/device-models/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(model),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to update device model');
  return response.json();
};

export const deleteDeviceModel = async (id: string): Promise<void> => {
  const response = await fetch(`/api/admin/device-models/${id}`, { method: 'DELETE' });
  if (!response.ok) throw new Error('Failed to delete device model');
};

// Pushes an immediate "check for update now" signal to every currently-connected radio of one
// hardware model, and flags any offline ones to get the same push the moment they reconnect
// (Model Catalog's APK Repository "Update ALL" action).
export const pushUpdateToModel = async (modelName: string): Promise<{ pushed: number; offline: number; total: number }> => {
  const response = await fetch(`/api/admin/device-models/${encodeURIComponent(modelName)}/push-update`, { method: 'POST' });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to push update to model');
  return response.json();
};

// APK Repository (Settings > Model Catalog > "APK Repository") - published builds, one per
// hardware model, that radios of that model self-install OTA (see documentation/protocol.md).
export interface AppUpdate {
  id: string;
  device_model_id: string;
  model_name?: string | null;
  version_code: number;
  version_name: string;
  sha256: string;
  file_size_bytes: number;
  release_notes?: string | null;
  // Which version radios of this model currently self-update to - not necessarily the highest
  // version_code, since an admin can roll back to an older one (see rollbackAppUpdate below).
  is_active: boolean;
  created_at: string;
  // Read-only artifact evidence recorded by the server (C3.3/C3.4), never entered by an admin.
  // Absent only from an older server.
  artifact?: AppUpdateArtifact;
}

// What the server recorded about the release's concrete APK. target_* are null unless the APK
// itself declared the build-target marker. signer_policy_status is the recorded signer's CURRENT
// compatibility policy for the release's model (server-derived); it never changes the evidence.
export interface AppUpdateArtifact {
  status: string; // VALIDATED | LEGACY | PENDING_INSPECTION (anything else: unknown)
  package_name: string | null;
  signer_cert_sha256: string | null;
  signer_policy_status: string | null; // ACTIVE | RETIRED | NOT_ALLOWED
  target_schema: string | null;
  target_manufacturer_code: string | null;
  target_model_code: string | null;
  checked_at: string | null;
  validator_version: string | null;
  validator_contract_version: string | null;
}

// A non-2xx API response, keeping the HTTP status and raw body so the caller can map the
// server's stable "CODE: message" errors (and proxy errors without one).
export class ApiResponseError extends Error {
  constructor(readonly status: number, readonly body: string, fallback: string) {
    super(body.trim() || fallback);
    this.name = 'ApiResponseError';
  }
}

export const getAppUpdates = async (): Promise<AppUpdate[]> => {
  const response = await fetch('/api/admin/app-updates');
  if (!response.ok) throw new Error('Failed to fetch app updates');
  return (await response.json()) ?? [];
};

// Trusted ingestion (documentation/protocol.md): only admin-controlled values are sent. Version,
// package, signer and build target are read from the APK by the server. No client timeout: the
// response arrives only after upload, validation and publication have finished.
export const buildAppUpdateForm = (params: { deviceModelId: string; releaseNotes?: string; file: File }): FormData => {
  const form = new FormData();
  form.append('device_model_id', params.deviceModelId);
  if (params.releaseNotes) form.append('release_notes', params.releaseNotes);
  form.append('file', params.file);
  return form;
};

export const publishAppUpdate = async (params: { deviceModelId: string; releaseNotes?: string; file: File }): Promise<AppUpdate> => {
  const response = await fetch('/api/admin/app-updates', { method: 'POST', body: buildAppUpdateForm(params) });
  if (!response.ok) throw new ApiResponseError(response.status, await response.text(), 'Failed to publish app update');
  return response.json();
};

export const deleteAppUpdate = async (id: string): Promise<void> => {
  const response = await fetch(`/api/admin/app-updates/${id}`, { method: 'DELETE' });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to delete app update');
};

export const updateAppUpdateReleaseNotes = async (id: string, releaseNotes: string): Promise<AppUpdate> => {
  const response = await fetch(`/api/admin/app-updates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ release_notes: releaseNotes || null }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to update release notes');
  return response.json();
};

// "Roll back to this version" (or re-promote a newer one) - marks this build as the one radios
// of its device model should self-update to, whether its version_code is higher or lower than
// what they currently have.
export const rollbackAppUpdate = async (id: string): Promise<AppUpdate> => {
  const response = await fetch(`/api/admin/app-updates/${id}/activate`, { method: 'POST' });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to roll back');
  return response.json();
};

// Reuses the same radio-facing download route the Android app itself uses (documentation/protocol.md
// "OTA app updates") - works fine from an authenticated admin session too, no separate endpoint.
export const downloadAppUpdateUrl = (id: string): string => `/api/updates/${id}/download`;

// OTA self-check toggle (APK Repository "Disable OTA self-check" button) - while disabled,
// publishing a new build never auto-activates it, so nothing self-installs to any radio; the
// previously active build keeps serving every self-check until an admin explicitly picks "Set as
// Current" on a build (rollbackAppUpdate above, never gated by this toggle).
export const getOtaSelfCheckEnabled = async (): Promise<boolean> => {
  const response = await fetch('/api/admin/app-updates/config');
  if (!response.ok) throw new Error('Failed to fetch OTA self-check setting');
  const data = await response.json();
  return data.ota_self_check_enabled;
};

export const setOtaSelfCheckEnabled = async (enabled: boolean): Promise<boolean> => {
  const response = await fetch('/api/admin/app-updates/config', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ota_self_check_enabled: enabled }),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to update OTA self-check setting');
  const data = await response.json();
  return data.ota_self_check_enabled;
};

// LED Outputs catalog (Settings > Model Catalog > "LED Outputs") - the global, model-independent
// vocabulary of LED outputs (color + state, e.g. "Red / On"). OEM intents are NOT here: each
// device model maps the outputs it supports to its own intent (DeviceModel.led_mappings).
export interface LedOutput {
  id: string;
  color: string;
  state: string;
  description?: string | null;
  sort_order: number;
  // How many device models map this output (read-only). A mapped output cannot be deleted.
  model_count: number;
  created_at: string;
  updated_at: string;
}

export type LedOutputInput = Pick<LedOutput, 'color' | 'state' | 'description' | 'sort_order'>;

export const getLedOutputs = async (): Promise<LedOutput[]> => {
  const response = await fetch('/api/admin/led-outputs');
  if (!response.ok) throw new Error('Failed to fetch LED outputs');
  return (await response.json()) ?? [];
};

export const createLedOutput = async (output: LedOutputInput): Promise<LedOutput> => {
  const response = await fetch('/api/admin/led-outputs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(output),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to create LED output');
  return response.json();
};

export const updateLedOutput = async (id: string, output: LedOutputInput): Promise<LedOutput> => {
  const response = await fetch(`/api/admin/led-outputs/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(output),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to update LED output');
  return response.json();
};

export const deleteLedOutput = async (id: string): Promise<void> => {
  const response = await fetch(`/api/admin/led-outputs/${id}`, { method: 'DELETE' });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to delete LED output');
};

// Button Functions (Settings > Model Catalog > "Button Functions") - a global catalog of
// logical hardware-button function names, independent of any specific device model. Intents
// (e.g. press/release) are defined per device model, not here - see DeviceModelButtonIntent.
export interface ButtonFunction {
  id: string;
  name: string;
  description?: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export type ButtonFunctionInput = Omit<ButtonFunction, 'id' | 'created_at' | 'updated_at'>;

export const getButtonFunctions = async (): Promise<ButtonFunction[]> => {
  const response = await fetch('/api/admin/button-functions');
  if (!response.ok) throw new Error('Failed to fetch button functions');
  return (await response.json()) ?? [];
};

export const createButtonFunction = async (fn: ButtonFunctionInput): Promise<ButtonFunction> => {
  const response = await fetch('/api/admin/button-functions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fn),
  });
  if (!response.ok) throw new Error('Failed to create button function');
  return response.json();
};

export const updateButtonFunction = async (id: string, fn: ButtonFunctionInput): Promise<ButtonFunction> => {
  const response = await fetch(`/api/admin/button-functions/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fn),
  });
  if (!response.ok) throw new Error('Failed to update button function');
  return response.json();
};

export const deleteButtonFunction = async (id: string): Promise<void> => {
  const response = await fetch(`/api/admin/button-functions/${id}`, { method: 'DELETE' });
  if (!response.ok) throw new Error('Failed to delete button function');
};

// Provisioning Tokens API
export const getProvisioningTokens = async (orgId: string): Promise<ProvisioningToken[]> => {
  const response = await fetch(`/api/admin/orgs/${orgId}/provisioning-tokens`);
  if (!response.ok) throw new Error('Failed to fetch tokens');
  // Backend returns null (not []) for an empty list; normalize to an array.
  return (await response.json()) ?? [];
};

export const createProvisioningTokens = async (orgId: string, count: number, expiryHours: number): Promise<ProvisioningToken[]> => {
  const response = await fetch(`/api/admin/orgs/${orgId}/provisioning-tokens`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ count, expiry_hours: expiryHours }),
  });
  if (!response.ok) throw new Error('Failed to create tokens');
  return response.json();
};

export const revokeProvisioningToken = async (orgId: string, tokenId: string): Promise<void> => {
  const response = await fetch(`/api/admin/orgs/${orgId}/provisioning-tokens/${tokenId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to revoke token');
};

// Activity Logs API
export const getFloorEvents = async (orgId: string, limit: number = 100): Promise<FloorEvent[]> => {
  const response = await fetch(`/api/admin/orgs/${orgId}/floor-events?limit=${limit}`);
  if (!response.ok) throw new Error('Failed to fetch floor events');
  // Backend returns null (not []) for an empty list; normalize to an array.
  return (await response.json()) ?? [];
};

// Pending Radios API (Device Approval Workflow)
export const getPendingRadios = async (orgId: string): Promise<PendingRadio[]> => {
  const response = await fetch(`/api/admin/orgs/${orgId}/pending-radios`);
  if (!response.ok) throw new Error('Failed to fetch pending radios');
  // Backend returns null (not []) for an empty list; normalize to an array.
  return (await response.json()) ?? [];
};

export const approveRadio = async (
  orgId: string,
  pendingRadioId: string,
  approval: {
    name: string;
    radio_id: string;
    account_id?: string | null;
  }
): Promise<Radio> => {
  const response = await fetch(`/api/admin/orgs/${orgId}/pending-radios/${pendingRadioId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(approval),
  });
  if (!response.ok) throw new Error('Failed to approve radio');
  return response.json();
};

export const rejectRadio = async (orgId: string, pendingRadioId: string, reason?: string): Promise<void> => {
  const response = await fetch(`/api/admin/orgs/${orgId}/pending-radios/${pendingRadioId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || '' }),
  });
  if (!response.ok) throw new Error('Failed to reject radio');
};

// Emergency alerts (manual SOS + automatic fall detection - both share the sos_events table,
// distinguished by sos_type; see documentation/protocol.md "Fall detection"). Used by the
// dispatcher-facing FallAlarmBanner (App.tsx).
export interface SosEvent {
  id: string;
  org_id: string;
  inrico_radio_id: string;
  sos_type: string; // manual, panic_button, low_battery, network_error, fall_detected
  description?: string;
  latitude?: number | null; // absent: no fresh position (<= 30 s) accompanied the alarm
  longitude?: number | null;
  location_timestamp?: string | null; // the GPS fix's own time; absent with a position: unknown (older app)
  location_accuracy_m?: number | null;
  acknowledged: boolean;
  acknowledged_by?: string | null;
  acknowledged_at?: string | null;
  resolved: boolean;
  resolved_at?: string | null;
  created_at: string;
  radio_name?: string | null;
  human_radio_id?: string | null;
}

export const getUnresolvedSosEvents = async (orgId: string): Promise<SosEvent[]> => {
  const response = await orgFetch(orgId, '/api/inrico/sos/events');
  if (!response.ok) throw new Error('Failed to fetch SOS/fall events');
  const data = await response.json();
  // Backend returns a raw JSON array (or null for an empty result) - see
  // GetSOSEventsHandler/documentation/protocol.md "Fall detection".
  return (Array.isArray(data) ? data : data?.events ?? []) as SosEvent[];
};

// Central "Afstil/Resolve alarm" from the Management/Admin or Dispatcher client (see
// documentation/protocol.md "Fall detection / Man Down") - requires an admin or dispatcher role
// (enforced server-side via the authenticated user's role claim, not this header). Pushes a
// fall_alarm_resolved WebSocket message to the reporting radio if it's currently connected.
export const resolveSosEvent = async (orgId: string, eventId: string): Promise<void> => {
  const response = await orgFetch(orgId, `/api/sos/${eventId}/resolve`, { method: 'POST' });
  if (!response.ok) throw new Error('Failed to resolve SOS/fall event');
};

// Organizations/Accounts API
export const getAllOrganizations = async (): Promise<Organization[]> => {
  const response = await fetch('/api/admin/organizations');
  if (!response.ok) throw new Error('Failed to fetch organizations');
  // Backend returns null (not []) for an empty list; normalize to an array.
  return (await response.json()) ?? [];
};

export const getOrganization = async (orgId: string): Promise<Organization> => {
  const response = await fetch(`/api/admin/organizations/${orgId}`);
  if (!response.ok) throw new Error('Failed to fetch organization');
  return response.json();
};

// Networks are created, renamed and deleted under their customer (services/tenancy.ts); the
// bodyless create (which would also create a new customer) and the unconfirmed delete are gone.

// Sync APIs
export interface SyncResult {
  status: 'success' | 'error' | 'partial';
  message: string;
  summary?: {
    total_booking_companies: number;
    synced: number;
    created: number;
    updated: number;
    failed: number;
  };
}

export const syncBookingCompanies = async (): Promise<SyncResult> => {
  const response = await fetch('/api/admin/organizations/sync/booking', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Failed to sync companies from booking system');
  return response.json();
};

export const getSyncStatus = async (): Promise<{ synced_at: string; status: string; summary: SyncResult['summary'] }> => {
  const response = await fetch('/api/admin/organizations/sync/status');
  if (!response.ok) throw new Error('Failed to get sync status');
  return response.json();
};

// Inrico Radio Authorization API
// A pending device now has no org until an admin approves+assigns one (see documentation/decisions.md),
// so this lists across all orgs rather than being scoped to one. The unused param is kept only
// for source compatibility with the legacy, unrouted PendingRadiosManager.tsx.
export const getPendingInricoRadios = async (_orgId?: string): Promise<InricoRadio[]> => {
  const response = await fetch(`/api/admin/inrico/radios/pending`);
  if (!response.ok) throw new Error('Failed to fetch pending radios');
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

// Rejected devices (Assign Radios > "Rejected" pool) - admin-rejected pending devices, kept
// separate from the pending list so "Reject" isn't a no-op, and still approvable later.
export const getRejectedInricoRadios = async (): Promise<InricoRadio[]> => {
  const response = await fetch(`/api/admin/inrico/radios/rejected`);
  if (!response.ok) throw new Error('Failed to fetch rejected radios');
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const rejectInricoRadio = async (androidId: string): Promise<void> => {
  const response = await fetch(`/api/admin/inrico/${androidId}/reject`, { method: 'POST' });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to reject radio');
};

// _orgId is unused (kept only for source compatibility with the legacy PendingRadiosManager.tsx) -
// android_id alone already uniquely identifies the device, and it has no org until assigned.
export const authorizeInricoRadio = async (
  _orgId: string,
  androidId: string,
  authorized: boolean,
  name?: string,
  radioId?: string
): Promise<void> => {
  const body: any = { authorized };
  
  // When approving, include name and radio_id
  if (authorized) {
    if (!name || !radioId) {
      throw new Error('Name and Radio ID are required when approving');
    }
    body.name = name;
    body.radio_id = radioId;
  }

  const response = await fetch(`/api/admin/inrico/${androidId}/authorize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error((await response.text()) || 'Failed to update radio authorization');
};

// ==========================================================================
// Operational event log / recordings / conversations
// See documentation/architecture.md - the event log is the authoritative operational timeline;
// recordings capture individual PTT transmissions; conversations group related recordings.
// ==========================================================================

export interface EventLogEntry {
  id: string;
  timestamp: string;
  event_type: string;
  severity: 'information' | 'warning' | 'emergency' | 'error';
  org_id: string;
  talk_group_id?: string | null;
  radio_id?: string | null;
  user_id?: string | null;
  session_id?: string | null;
  ptt_transmission_id?: string | null;
  recording_id?: string | null;
  // Best-effort join to recordings.status, present only when recording_id is set - lets the UI
  // show "Recording expired"/"unavailable" instead of a broken Play/Download control.
  recording_status?: 'recording' | 'completed' | 'incomplete' | 'failed' | 'expired' | null;
  metadata?: Record<string, unknown>;
}

export interface EventLogFilters {
  start?: string; // RFC3339
  end?: string;
  talk_group_id?: string;
  radio_id?: string;
  user_id?: string;
  event_type?: string;
  severity?: string;
  has_recording?: boolean;
  limit?: number;
  offset?: number;
}

const buildQuery = (params: object): string => {
  const usp = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([k, v]) => {
    if (v !== undefined && v !== '') usp.set(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
};

export const getEventLog = async (
  orgId: string,
  filters: EventLogFilters = {}
): Promise<{ events: EventLogEntry[]; total: number; limit: number; offset: number }> => {
  const response = await orgFetch(orgId, `/api/admin/log${buildQuery(filters)}`);
  if (!response.ok) throw new Error('Failed to fetch event log');
  return response.json();
};

export interface Recording {
  id: string;
  org_id: string;
  talk_group_id?: string | null;
  radio_id?: string | null;
  // Joined in for display only - radio_id itself is an internal id, not human-readable.
  radio_human_id?: string | null;
  radio_name?: string | null;
  user_id?: string | null;
  ptt_transmission_id: string;
  started_at: string;
  ended_at?: string | null;
  duration_ms?: number | null;
  network_codec: string;
  stored_codec: string;
  sample_rate: number;
  channel_count: number;
  container_format: string;
  file_size_bytes?: number | null;
  storage_key?: string | null;
  status: 'recording' | 'completed' | 'incomplete' | 'failed' | 'expired';
  failure_reason?: string | null;
  created_at: string;
}

export interface RecordingFilters {
  start?: string;
  end?: string;
  talk_group_id?: string;
  radio_id?: string;
  conversation_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export const getRecordings = async (
  orgId: string,
  filters: RecordingFilters = {}
): Promise<{ recordings: Recording[]; total: number; limit: number; offset: number }> => {
  const response = await orgFetch(orgId, `/api/admin/recordings${buildQuery(filters)}`);
  if (!response.ok) throw new Error('Failed to fetch recordings');
  return response.json();
};

// Streaming download/playback URL, following the same plain-path pattern as
// getBackupDownloadUrl - tenant isolation is enforced server-side from the authenticated
// request context (X-Org-ID is set from the validated JWT by AuthMiddleware in production;
// only DEV_MODE trusts a client-supplied header at all), not from anything embedded in this URL.
export const getRecordingDownloadUrl = (recordingId: string): string =>
  `/api/admin/recordings/${encodeURIComponent(recordingId)}/download`;

export interface Conversation {
  id: string;
  org_id: string;
  talk_group_id?: string | null;
  started_at: string;
  ended_at: string;
  duration_ms: number;
  recording_count: number;
  export_format?: string | null;
  export_generated_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConversationMember {
  recording_id: string;
  sequence: number;
  radio_id?: string | null;
  started_at: string;
  ended_at?: string | null;
  duration_ms?: number | null;
  status: string;
}

export interface ConversationFilters {
  start?: string;
  end?: string;
  talk_group_id?: string;
  limit?: number;
  offset?: number;
}

export const getConversations = async (
  orgId: string,
  filters: ConversationFilters = {}
): Promise<{ conversations: Conversation[]; total: number; limit: number; offset: number }> => {
  const response = await orgFetch(orgId, `/api/admin/conversations${buildQuery(filters)}`);
  if (!response.ok) throw new Error('Failed to fetch conversations');
  return response.json();
};

export const getConversationDetail = async (
  orgId: string,
  conversationId: string
): Promise<{ conversation: Conversation; recordings: ConversationMember[] }> => {
  const response = await orgFetch(orgId, `/api/admin/conversations/${encodeURIComponent(conversationId)}`);
  if (!response.ok) throw new Error('Failed to fetch conversation');
  return response.json();
};

export const getConversationDownloadUrl = (conversationId: string): string =>
  `/api/admin/conversations/${encodeURIComponent(conversationId)}/download`;

export const createConversation = async (
  orgId: string,
  talkGroupId: string,
  recordingIds: string[]
): Promise<{ id: string }> => {
  const response = await orgFetch(orgId, '/api/admin/conversations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ talk_group_id: talkGroupId, recording_ids: recordingIds }),
  });
  if (!response.ok) throw new Error('Failed to create conversation');
  return response.json();
};

// ==========================================================================
// Retention / cleanup subsystem
// See documentation/architecture.md "Cleanup & retention". Global (not per-network) - these endpoints
// are not org-scoped.
// ==========================================================================

export interface CleanupRetention {
  recording_retention_days: number;
  log_retention_days: number;
  conversation_export_retention_days: number;
  direct_message_retention_days: number;
  // GPS location history; the newest fix of each radio in its current network is always kept.
  location_history_retention_days: number;
  temp_file_max_age_hours: number;
  orphan_file_min_age_hours: number;
  batch_size: number;
}

export interface CleanupSummary {
  started_at: string;
  completed_at: string;
  duration_ms: number;
  dry_run: boolean;
  result: 'success' | 'partial' | 'failed' | 'skipped';
  error?: string;
  stale_recordings_recovered: number;
  stale_recordings_failed: number;
  temp_files_removed: number;
  conversation_exports_removed: number;
  direct_messages_removed: number;
  location_history_rows_removed?: number; // absent from runs before location retention existed
  recordings_expired: number;
  recording_file_errors: number;
  missing_files_reconciled: number;
  log_rows_removed: number;
  orphan_files_removed: number;
}

export interface CleanupStatus {
  enabled: boolean;
  dry_run: boolean;
  interval_hours: number;
  retention: CleanupRetention;
  is_running: boolean;
  last_started_at?: string;
  last_completed_at?: string;
  last_result?: string;
  last_error?: string;
  last_summary?: CleanupSummary;
}

export const getCleanupStatus = async (): Promise<CleanupStatus> => {
  const response = await fetch('/api/admin/cleanup/status');
  if (!response.ok) throw new Error('Failed to fetch cleanup status');
  return response.json();
};

export const runCleanupNow = async (): Promise<CleanupSummary> => {
  const response = await fetch('/api/admin/cleanup/run', { method: 'POST' });
  if (!response.ok && response.status !== 409) throw new Error('Failed to run cleanup');
  return response.json();
};

// "Delete everything now" - a deliberate, immediate bulk purge distinct from the age-based
// retention run above. Never touches organizations/users/radios/groups/api_keys or recording
// rows (only recording audio files) - see documentation/decisions.md.
export type PurgeTarget = 'recordings' | 'logs' | 'temp_files' | 'exports' | 'backups' | 'direct_messages';

export interface PurgeSummary {
  started_at: string;
  completed_at: string;
  duration_ms: number;
  dry_run: boolean;
  result: 'success' | 'partial' | 'failed' | 'skipped';
  error?: string;
  recordings_expired?: number;
  recording_file_errors?: number;
  event_log_rows_removed?: number;
  system_log_rows_removed?: number;
  session_log_rows_removed?: number;
  floor_event_rows_removed?: number;
  sync_log_rows_removed?: number;
  temp_files_removed?: number;
  conversation_exports_removed?: number;
  direct_messages_removed?: number;
  backups_removed?: number;
  backup_errors?: number;
}

export const purgeNow = async (targets: PurgeTarget[], dryRun = false): Promise<PurgeSummary> => {
  const response = await fetch('/api/admin/cleanup/purge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targets, dry_run: dryRun }),
  });
  if (!response.ok && response.status !== 409) throw new Error('Failed to purge');
  return response.json();
};

export type CleanupRetentionUpdate = Partial<{
  enabled: boolean;
  dry_run: boolean;
  recording_retention_days: number;
  log_retention_days: number;
  conversation_export_retention_days: number;
  direct_message_retention_days: number;
  // GPS location history; the newest fix of each radio in its current network is always kept.
  location_history_retention_days: number;
  temp_file_max_age_hours: number;
  orphan_file_min_age_hours: number;
  interval_hours: number;
  batch_size: number;
}>;

export const updateCleanupRetention = async (update: CleanupRetentionUpdate): Promise<void> => {
  const response = await fetch('/api/admin/cleanup/retention', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(update),
  });
  if (!response.ok) throw new Error(await response.text() || 'Failed to save retention settings');
};

